/**
 * Release notes API — contract #31 (docs/05-dev-spec.md §4, task T.Z103).
 *
 *   #31 GET /api/release-notes → `{ data: { blocks: ReleaseNoteBlockDto[] } }`   (VIEWER and up)
 *
 * CONTRACT-FIRST: the DTOs below are mirrored 1:1 from the generated
 * `packages/shared/src/openapi/api-types.ts`
 * (`components.schemas.ReleaseNoteBlockDto` / `ReleaseNotesDto`), the same rule
 * `api/system-status.ts` states. The api serves the blocks of the repo's
 * `changelog.txt` (rule 0 of CLAUDE.md) in FILE order — newest first is the
 * file's own convention, not a sort the client can rely on the api having done
 * differently.
 *
 * The tolerant unwrap lives in `pages/settings/release-notes-model.ts`
 * (`parseReleaseNotes`) — this module value-imports `client.ts`
 * (`import.meta.env`), which the plain-Node `unit` project cannot load. The same
 * split puts `parseSystemStatus` next to the status panel.
 */

import { useQuery } from '@tanstack/react-query';

import { apiRequest } from './client';

/** `ReleaseNoteBlockDto` — one release of `changelog.txt`. */
export interface ReleaseNoteBlockDto {
  /** The release tag exactly as the file names it — the value the status footer's `version` reports. */
  tag: string;
  /** Release date, `YYYY-MM-DD`. */
  date: string;
  /** The free text after the date («ПРОД: …», «KIND: …»); null when the header carries none. */
  caption: string | null;
  /** The block's `- ` bullet lines, one feature per line, in file order. */
  items: string[];
}

/** The #31 payload. `blocks` is always present; `[]` means the api found no readable file. */
export interface ReleaseNotesDto {
  blocks: ReleaseNoteBlockDto[];
}

export const RELEASE_NOTES_PATH = '/release-notes';

/**
 * #31, RAW. Deliberately un-normalised: the unwrap is pure logic and lives in
 * `pages/settings/release-notes-model.ts`, where the unit project can reach it.
 */
export async function fetchReleaseNotes(signal?: AbortSignal): Promise<unknown> {
  // `apiRequest` (not a bare fetch) so a dead session behaves like everywhere
  // else in the app: a 401 is a full-page hop to the OAuth entry point.
  return apiRequest<unknown>('GET', RELEASE_NOTES_PATH, { signal });
}

const RELEASE_NOTES_ROOT = ['release', 'notes'] as const;

/**
 * #31 — fetched ONCE per mount session, never polled: the changelog changes only
 * when a new image rolls out, and a rollout reloads the SPA anyway. `retry: 0`
 * for the same reason the status panel does it — an api that cannot read its own
 * file answers `blocks: []`, and a transport error is not worth a second try.
 */
export function useReleaseNotes() {
  return useQuery({
    queryKey: RELEASE_NOTES_ROOT,
    queryFn: ({ signal }) => fetchReleaseNotes(signal),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    retry: 0,
  });
}
